import * as React from 'react'
import type { ReactNode, JSX } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'

import { Dialog } from '../Dialog'
import type { DialogTitleProps, DialogContentProps } from '../Dialog'
import { Flex } from '../Flex'
import styles from './UpgradeDialog.module.css'
import { clsx } from 'clsx'
import { Heading } from '../Heading'
import { Text } from '../Text'
import type { TextProps } from '../Text'

// Dialog trigger doesn't need additional props, so we can use it directly.
const UpgradeDialogTrigger = Dialog.Trigger
UpgradeDialogTrigger.displayName = 'UpgradeDialog.Trigger'

interface UpgradeDialogProps {
  children: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

/**
 * This dialog is used to show upgrade options to the user. It has standardised layout to make sure the content is consistent.
 */
const UpgradeDialog = (props: UpgradeDialogProps): JSX.Element => (
  <Dialog {...props} />
)

UpgradeDialog.displayName = 'UpgradeDialog'

interface UpgradeDialogTextContentProps {
  children: ReactNode
  className?: string
}

const UpgradeDialogTextContent = React.forwardRef<
  HTMLDivElement,
  UpgradeDialogTextContentProps
>(({ children, className }, ref) => (
  <Flex
    gap="md"
    direction="column"
    align="start"
    justify="center"
    className={clsx(styles.textContent, className)}
    ref={ref}
  >
    {children}
  </Flex>
))

UpgradeDialogTextContent.displayName = 'UpgradeDialog.TextContent'

interface UpgradeDialogContentProps extends DialogContentProps {
  children: ReactNode
  className?: string
  size?: DialogContentProps['size']
}

const UpgradeDialogContent = React.forwardRef<
  HTMLDivElement,
  UpgradeDialogContentProps
>(({ children, className, size = 'large', ...props }, ref) => (
  <Dialog.Content
    size={size}
    className={`${styles.dialogContent} ${className || ''}`}
    ref={ref}
    {...props}
  >
    {children}
    <Dialog.CloseButton className={styles.closeButton} />
  </Dialog.Content>
))

UpgradeDialogContent.displayName = 'UpgradeDialog.Content'

type UpgradeDialogImageProps = {
  children: ReactNode
  className?: string
}

const UpgradeDialogImage = React.forwardRef<
  HTMLDivElement,
  UpgradeDialogImageProps
>(({ children, className, ...props }, ref) => (
  <div className={clsx(styles.image, className)} {...props} ref={ref}>
    {children}
  </div>
))

UpgradeDialogImage.displayName = 'UpgradeDialog.Image'

type UpgradeDialogVideoProps = {
  children: ReactNode
  className?: string
}

const UpgradeDialogVideo = React.forwardRef<
  HTMLDivElement,
  UpgradeDialogVideoProps
>(({ children, className, ...props }, ref) => (
  <div className={clsx(styles.video, className)} {...props} ref={ref}>
    {children}
  </div>
))

UpgradeDialogVideo.displayName = 'UpgradeDialog.Video'

interface UpgradeDialogActionsProps {
  children: ReactNode
  className?: string
}

const UpgradeDialogActions = React.forwardRef<
  HTMLDivElement,
  UpgradeDialogActionsProps
>(({ children, className }, ref) => (
  <div className={clsx(styles.actions, className)} ref={ref}>
    {children}
  </div>
))

UpgradeDialogActions.displayName = 'UpgradeDialog.Actions'

type DialogTitleElement = React.ElementRef<typeof Heading>

const UpgradeDialogTitle = React.forwardRef<
  DialogTitleElement,
  DialogTitleProps
>(({ className, children, ...props }, forwardedRef) => (
  <DialogPrimitive.Title asChild>
    <Heading size="lg" className={className} {...props} ref={forwardedRef}>
      {children}
    </Heading>
  </DialogPrimitive.Title>
))

UpgradeDialogTitle.displayName = 'UpgradeDialog.Title'

type UpgradeDialogDescriptionElement = React.ElementRef<typeof Text>

interface UpgradeDialogDescriptionProps extends TextProps {
  children: ReactNode
  className?: string
}

const UpgradeDialogDescription = React.forwardRef<
  UpgradeDialogDescriptionElement,
  UpgradeDialogDescriptionProps
>(({ className, children, as: _as, ...props }, forwardedRef) => (
  <DialogPrimitive.Description asChild>
    <Text
      size="lg"
      color="subtle"
      className={className}
      {...props}
      ref={forwardedRef}
    >
      {children}
    </Text>
  </DialogPrimitive.Description>
))

UpgradeDialogDescription.displayName = 'UpgradeDialog.Description'

const UpgradeDialogObject = Object.assign(UpgradeDialog, {
  Trigger: UpgradeDialogTrigger,
  Content: UpgradeDialogContent,
  TextContent: UpgradeDialogTextContent,
  Image: UpgradeDialogImage,
  Title: UpgradeDialogTitle,
  Description: UpgradeDialogDescription,
  Actions: UpgradeDialogActions,
  Video: UpgradeDialogVideo,
})

export { UpgradeDialogObject as UpgradeDialog }
export type {
  UpgradeDialogProps,
  UpgradeDialogTextContentProps,
  UpgradeDialogContentProps,
  UpgradeDialogImageProps,
  UpgradeDialogVideoProps,
}
